import React, { useEffect, useMemo, useRef, useState } from "react";
import * as productService from "../../services/productService";
import * as categoryService from "../../services/categoryService";
import ProductForm from "../../components/Dashboard/ProductForm";

const styles = {
  page: {
    padding: "28px 36px",
    background: "#fbf7fa",
    minHeight: "100%",
  },
  heroTitle: {
    fontSize: "24px",
    fontWeight: 800,
    color: "#8f1632",
    margin: 0,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  heroSubtitle: {
    color: "#ff4f9a",
    marginTop: 8,
    marginBottom: 28,
    fontWeight: 600,
  },
  card: {
    background: "#fff",
    border: "1px solid #f1d5df",
    borderRadius: 18,
    padding: 22,
    boxShadow: "0 8px 20px rgba(143, 22, 50, 0.04)",
    marginBottom: 20,
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    margin: 0,
    color: "#111827",
  },
  desc: {
    color: "#4b5563",
    marginTop: 6,
    lineHeight: 1.6,
  },
  row: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  },
  input: {
    minWidth: 220,
    flex: 1,
    border: "1px solid #ead5dd",
    borderRadius: 12,
    padding: "11px 14px",
    background: "#fff",
  },
  btn: {
    border: "1px solid #f3c7d7",
    background: "#fff",
    color: "#8f1632",
    borderRadius: 12,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  btnPrimary: {
    border: "1px solid #8f1632",
    background: "#8f1632",
    color: "#fff",
    borderRadius: 12,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  btnDanger: {
    border: "1px solid #ef4444",
    background: "#fff",
    color: "#b91c1c",
    borderRadius: 12,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  tableWrap: {
    overflowX: "auto",
    border: "1px solid #f2e3e8",
    borderRadius: 16,
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    minWidth: 1080,
  },
  th: {
    textAlign: "left",
    fontSize: 13,
    color: "#6b7280",
    padding: "14px 16px",
    borderBottom: "1px solid #f2e3e8",
    background: "#fff9fb",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "14px 16px",
    borderBottom: "1px solid #f7edf1",
    verticalAlign: "top",
    color: "#111827",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "#ecfdf3",
    color: "#027a48",
    fontWeight: 700,
    fontSize: 12,
  },
  badgeMuted: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "#f3f4f6",
    color: "#4b5563",
    fontWeight: 700,
    fontSize: 12,
  },
  actionRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  empty: {
    padding: 18,
    border: "1px dashed #f1c2d3",
    borderRadius: 14,
    color: "#6b7280",
    background: "#fff",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(17,24,39,0.45)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: 28,
    overflowY: "auto",
    zIndex: 1000,
  },
  modalCard: {
    width: "min(1080px, 100%)",
    background: "#fff",
    borderRadius: 20,
    border: "1px solid #f1d5df",
    boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
    padding: 22,
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    marginBottom: 18,
  },
  infoBox: {
    border: "1px solid #f2e3e8",
    background: "#fffdfd",
    borderRadius: 14,
    padding: 14,
    marginTop: 14,
  },
  logList: {
    marginTop: 10,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    maxHeight: 220,
    overflowY: "auto",
  },
  logItem: {
    border: "1px solid #f3e4ea",
    borderRadius: 10,
    padding: "8px 10px",
    fontSize: 13,
    background: "#fff",
  },
};

function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function getCount(payload) {
  if (typeof payload?.count === "number") return payload.count;
  if (Array.isArray(payload)) return payload.length;
  if (Array.isArray(payload?.results)) return payload.results.length;
  if (Array.isArray(payload?.data)) return payload.data.length;
  return 0;
}

function makeSlug(text) {
  return String(text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function parseNumber(value, fallback = 0) {
  const num = Number(String(value ?? "").replace(/,/g, "."));
  return Number.isFinite(num) ? num : fallback;
}

function parseBool(value, fallback = true) {
  const v = String(value ?? "").trim().toLowerCase();
  if (!v) return fallback;
  if (["1", "true", "ya", "yes", "aktif"].includes(v)) return true;
  if (["0", "false", "tidak", "no", "nonaktif"].includes(v)) return false;
  return fallback;
}

function parseCsv(text) {
  const lines = String(text || "")
    .replace(/\r/g, "")
    .split("\n")
    .filter((line) => line.trim() !== "");

  if (!lines.length) return [];

  const rows = lines.map((line) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      const next = line[i + 1];

      if (char === '"' && inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current);
        current = "";
      } else {
        current += char;
      }
    }

    result.push(current);
    return result.map((item) => item.trim());
  });

  const headers = rows[0].map((h) => h.trim());
  return rows.slice(1).map((cols) => {
    const obj = {};
    headers.forEach((header, idx) => {
      obj[header] = cols[idx] ?? "";
    });
    return obj;
  });
}

function buildCategoryMaps(categories) {
  const byId = new Map();
  const bySlug = new Map();
  const byName = new Map();

  categories.forEach((item) => {
    byId.set(String(item.id), item);
    bySlug.set(String(item.slug || "").toLowerCase(), item);
    byName.set(String(item.name || "").toLowerCase(), item);
  });

  return { byId, bySlug, byName };
}

function normalizeBatchRow(row, categoryMaps) {
  const categoryValue =
    row.category_id || row.category_slug || row.category_name || "";

  let categoryId = null;

  if (categoryMaps.byId.has(String(categoryValue))) {
    categoryId = Number(categoryValue);
  } else if (categoryMaps.bySlug.has(String(categoryValue).toLowerCase())) {
    categoryId = categoryMaps.bySlug.get(String(categoryValue).toLowerCase()).id;
  } else if (categoryMaps.byName.has(String(categoryValue).toLowerCase())) {
    categoryId = categoryMaps.byName.get(String(categoryValue).toLowerCase()).id;
  }

  const name = row.name || "";
  const slug = row.slug || makeSlug(name);

  return {
    name,
    slug,
    sku: row.sku || "",
    short_description: row.short_description || "",
    description: row.description || "",
    price: parseNumber(row.price, 0),
    original_price: parseNumber(row.original_price, 0),
    stock: Math.round(parseNumber(row.stock, 0)),
    unit: row.unit || "pcs",
    featured_image: row.featured_image || "",
    is_featured: parseBool(row.is_featured, false),
    is_active: parseBool(row.is_active, true),
    category_id: categoryId,
    rating: parseNumber(row.rating, 5),
    sold_count: Math.round(parseNumber(row.sold_count, 0)),
    condition: row.condition || "baru",
    weight_gram: Math.round(parseNumber(row.weight_gram, 0)),
    video_url: row.video_url || "",
  };
}

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [selectedIds, setSelectedIds] = useState([]);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [savingForm, setSavingForm] = useState(false);

  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchFileName, setBatchFileName] = useState("");
  const [batchRows, setBatchRows] = useState([]);
  const [batchLogs, setBatchLogs] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);

        const [productPayload, categoryPayload] = await Promise.all([
          productService.getAdminProducts({ page: 1, page_size: 200 }),
          categoryService.getAdminCategories({ page: 1, page_size: 200 }),
        ]);

        if (!active) return;

        setProducts(asArray(productPayload));
        setCategories(asArray(categoryPayload));
      } catch (error) {
        if (!active) return;
        setProducts([]);
        setCategories([]);
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  const filteredProducts = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return products;

    return products.filter((item) => {
      const hay = [
        item?.name,
        item?.slug,
        item?.sku,
        item?.category_name,
        item?.category?.name,
        item?.short_description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(keyword);
    });
  }, [products, search]);

  const allVisibleSelected =
    filteredProducts.length > 0 &&
    filteredProducts.every((item) => selectedIds.includes(item.id));

  async function reloadProducts() {
    const payload = await productService.getAdminProducts({ page: 1, page_size: 200 });
    setProducts(asArray(payload));
  }

  function openAddModal() {
    setEditingItem(null);
    setShowFormModal(true);
  }

  function openEditModal(item) {
    setEditingItem(item);
    setShowFormModal(true);
  }

  async function handleSaveProduct(payload) {
    try {
      setSavingForm(true);

      if (editingItem?.id) {
        await productService.updateProduct(editingItem.id, payload);
      } else {
        await productService.createProduct(payload);
      }

      setShowFormModal(false);
      setEditingItem(null);
      await reloadProducts();
    } catch (error) {
      alert(error?.message || "Gagal menyimpan produk.");
    } finally {
      setSavingForm(false);
    }
  }

  async function handleDeleteOne(item) {
    const ok = window.confirm(`Hapus produk "${item?.name}"?`);
    if (!ok) return;

    try {
      await productService.deleteProduct(item.id);
      setProducts((prev) => prev.filter((row) => row.id !== item.id));
      setSelectedIds((prev) => prev.filter((id) => id !== item.id));
    } catch (error) {
      alert(error?.message || "Gagal menghapus produk.");
    }
  }

  async function handleDeleteSelected() {
    if (!selectedIds.length) {
      alert("Belum ada produk yang dipilih.");
      return;
    }

    const ok = window.confirm(
      `Hapus ${selectedIds.length} produk yang dipilih?`
    );
    if (!ok) return;

    const errors = [];
    for (let i = 0; i < selectedIds.length; i += 1) {
      const id = selectedIds[i];
      try {
        await productService.deleteProduct(id);
      } catch (error) {
        errors.push(id);
      }
    }

    await reloadProducts();
    setSelectedIds([]);

    if (errors.length) {
      alert(`${errors.length} produk gagal dihapus.`);
    }
  }

  function toggleSelectOne(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }

  function toggleSelectAllVisible() {
    if (allVisibleSelected) {
      const visibleIds = filteredProducts.map((item) => item.id);
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }

    const merged = new Set([...selectedIds, ...filteredProducts.map((item) => item.id)]);
    setSelectedIds(Array.from(merged));
  }

  function handleDownloadTemplate() {
    const csv = [
      [
        "name",
        "slug",
        "sku",
        "short_description",
        "description",
        "price",
        "original_price",
        "stock",
        "unit",
        "featured_image",
        "is_featured",
        "is_active",
        "category_slug",
        "rating",
        "sold_count",
        "condition",
        "weight_gram",
        "video_url",
      ].join(","),
      [
        '"Sensor Flow Meter IoT"',
        '"sensor-flow-meter-iot"',
        '"IOT-001"',
        '"Sensor flow meter untuk monitoring debit air real-time"',
        '"Sensor flow meter cocok untuk monitoring air rumah, depot, dan usaha"',
        "250000",
        "275000",
        "20",
        '"pcs"',
        '"/images/products/flow-meter.jpg"',
        "true",
        "true",
        '"sensor-iot"',
        "5",
        "0",
        '"baru"',
        "200",
        '""',
      ].join(","),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "template_batch_products_solusi_iot_cerdas.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleOpenBatchModal() {
    setBatchFileName("");
    setBatchRows([]);
    setBatchLogs([]);
    setShowBatchModal(true);
  }

  function handlePickBatchFile() {
    fileInputRef.current?.click();
  }

  async function handleBatchFileChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const rawRows = parseCsv(text);
      const categoryMaps = buildCategoryMaps(categories);
      const normalizedRows = rawRows.map((row, index) => ({
        __row: index + 2,
        ...normalizeBatchRow(row, categoryMaps),
      }));

      setBatchFileName(file.name);
      setBatchRows(normalizedRows);
      setBatchLogs([]);
    } catch (error) {
      alert("Gagal membaca file CSV.");
    } finally {
      event.target.value = "";
    }
  }

  async function handleRunBatchUpload() {
    if (!batchRows.length) {
      alert("Belum ada data batch yang siap diupload.");
      return;
    }

    setBatchRunning(true);
    setBatchLogs([]);

    const logs = [];

    for (let i = 0; i < batchRows.length; i += 1) {
      const row = batchRows[i];

      try {
        if (!row.name) {
          throw new Error("Nama produk kosong.");
        }
        if (!row.slug) {
          throw new Error("Slug kosong.");
        }

        await productService.createProduct({
          name: row.name,
          slug: row.slug,
          sku: row.sku,
          short_description: row.short_description,
          description: row.description,
          price: row.price,
          original_price: row.original_price,
          stock: row.stock,
          unit: row.unit,
          featured_image: row.featured_image,
          is_featured: row.is_featured,
          is_active: row.is_active,
          category_id: row.category_id,
          rating: row.rating,
          sold_count: row.sold_count,
          condition: row.condition,
          weight_gram: row.weight_gram,
          video_url: row.video_url,
        });

        logs.push({
          row: row.__row,
          status: "success",
          message: `Produk "${row.name}" berhasil dibuat.`,
        });
      } catch (error) {
        logs.push({
          row: row.__row,
          status: "error",
          message: error?.message || `Gagal upload produk "${row.name}".`,
        });
      }

      setBatchLogs([...logs]);
    }

    setBatchRunning(false);
    await reloadProducts();
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.heroTitle}>Dashboard Solusi IoT Cerdas</h1>
      <div style={styles.heroSubtitle}>
        Kelola data dan pengaturan untuk website Solusi IoT Cerdas.
      </div>

      <div style={styles.card}>
        <div style={styles.topBar}>
          <div>
            <h2 style={styles.title}>Kelola Produk IoT</h2>
            <div style={styles.desc}>
              Tambah produk manual atau batch upload CSV, serta pilih banyak
              produk untuk dihapus sekaligus.
            </div>
          </div>

          <div style={styles.row}>
            <button type="button" style={styles.btn} onClick={handleDownloadTemplate}>
              Download Template CSV
            </button>
            <button type="button" style={styles.btn} onClick={handleOpenBatchModal}>
              Batch Upload
            </button>
            <button type="button" style={styles.btnPrimary} onClick={openAddModal}>
              + Tambah Produk
            </button>
          </div>
        </div>

        <div style={{ ...styles.row, marginBottom: 16 }}>
          <input
            style={styles.input}
            placeholder="Cari produk, SKU, kategori..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <span style={styles.badgeMuted}>Total: {getCount(products)}</span>

          {selectedIds.length ? (
            <button type="button" style={styles.btnDanger} onClick={handleDeleteSelected}>
              Hapus Terpilih ({selectedIds.length})
            </button>
          ) : null}
        </div>

        {loading ? <div style={styles.empty}>Memuat data produk...</div> : null}

        {!loading && !filteredProducts.length ? (
          <div style={styles.empty}>Belum ada produk atau tidak ada hasil pencarian.</div>
        ) : null}

        {!loading && filteredProducts.length ? (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                    />
                  </th>
                  <th style={styles.th}>Produk</th>
                  <th style={styles.th}>SKU</th>
                  <th style={styles.th}>Kategori</th>
                  <th style={styles.th}>Harga</th>
                  <th style={styles.th}>Stok</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((item) => (
                  <tr key={item.id}>
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleSelectOne(item.id)}
                      />
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 800 }}>{item?.name || "-"}</div>
                      <div style={{ color: "#6b7280", fontSize: 13, marginTop: 6 }}>
                        {item?.slug || "-"}
                      </div>
                    </td>
                    <td style={styles.td}>{item?.sku || "-"}</td>
                    <td style={styles.td}>
                      {item?.category_name || item?.category?.name || "-"}
                    </td>
                    <td style={styles.td}>
                      Rp {Number(item?.price || 0).toLocaleString("id-ID")}
                    </td>
                    <td style={styles.td}>{item?.stock ?? 0}</td>
                    <td style={styles.td}>
                      <span style={item?.is_active === false ? styles.badgeMuted : styles.badge}>
                        {item?.is_active === false ? "Nonaktif" : "Aktif"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionRow}>
                        <button
                          type="button"
                          style={styles.btn}
                          onClick={() => openEditModal(item)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          style={styles.btnDanger}
                          onClick={() => handleDeleteOne(item)}
                        >
                          Hapus
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {showFormModal ? (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>
                  {editingItem?.id ? "Edit Produk" : "Tambah Produk"}
                </div>
                <div style={{ color: "#6b7280", marginTop: 6 }}>
                  Isi data produk secara manual.
                </div>
              </div>

              <button
                type="button"
                style={styles.btn}
                onClick={() => {
                  setShowFormModal(false);
                  setEditingItem(null);
                }}
              >
                Tutup
              </button>
            </div>

            <ProductForm
              initialData={editingItem}
              categories={categories}
              onSubmit={handleSaveProduct}
              onCancel={() => {
                setShowFormModal(false);
                setEditingItem(null);
              }}
              saving={savingForm}
            />
          </div>
        </div>
      ) : null}

      {showBatchModal ? (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>Batch Upload Produk</div>
                <div style={{ color: "#6b7280", marginTop: 6 }}>
                  Upload CSV, preview data, lalu kirim ke backend satu per satu.
                </div>
              </div>

              <button
                type="button"
                style={styles.btn}
                onClick={() => {
                  setShowBatchModal(false);
                  setBatchRows([]);
                  setBatchLogs([]);
                }}
              >
                Tutup
              </button>
            </div>

            <div style={styles.row}>
              <button type="button" style={styles.btn} onClick={handleDownloadTemplate}>
                Download Template CSV
              </button>

              <button type="button" style={styles.btnPrimary} onClick={handlePickBatchFile}>
                Pilih File CSV
              </button>

              <button
                type="button"
                style={styles.btn}
                onClick={handleRunBatchUpload}
                disabled={batchRunning}
              >
                {batchRunning ? "Sedang Upload..." : "Jalankan Batch Upload"}
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={handleBatchFileChange}
              />
            </div>

            <div style={styles.infoBox}>
              <div style={{ fontWeight: 700 }}>File</div>
              <div style={{ color: "#6b7280", marginTop: 6 }}>
                {batchFileName || "Belum ada file dipilih"}
              </div>

              <div style={{ marginTop: 12, fontWeight: 700 }}>
                Total baris siap upload: {batchRows.length}
              </div>

              {batchRows.length ? (
                <div style={{ marginTop: 12, overflowX: "auto" }}>
                  <table style={{ ...styles.table, minWidth: 800 }}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Row</th>
                        <th style={styles.th}>Nama</th>
                        <th style={styles.th}>Slug</th>
                        <th style={styles.th}>SKU</th>
                        <th style={styles.th}>Kategori ID</th>
                        <th style={styles.th}>Harga</th>
                        <th style={styles.th}>Stok</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batchRows.slice(0, 20).map((row) => (
                        <tr key={`${row.__row}-${row.slug}`}>
                          <td style={styles.td}>{row.__row}</td>
                          <td style={styles.td}>{row.name}</td>
                          <td style={styles.td}>{row.slug}</td>
                          <td style={styles.td}>{row.sku || "-"}</td>
                          <td style={styles.td}>{row.category_id || "-"}</td>
                          <td style={styles.td}>{row.price}</td>
                          <td style={styles.td}>{row.stock}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {!!batchLogs.length ? (
                <div style={styles.logList}>
                  {batchLogs.map((log, idx) => (
                    <div key={`${log.row}-${idx}`} style={styles.logItem}>
                      <strong>Row {log.row}</strong> —{" "}
                      <span
                        style={{
                          color: log.status === "success" ? "#027a48" : "#b91c1c",
                          fontWeight: 700,
                        }}
                      >
                        {log.status === "success" ? "SUCCESS" : "ERROR"}
                      </span>
                      <div style={{ marginTop: 4 }}>{log.message}</div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}